import React, { FC, useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { Link, useHistory, useLocation, useParams } from 'react-router-dom'
import MonthlyApplication from '../../../../components/report/detail/monthly/MonthlyApplication'
import MonthlyCategory from '../../../../components/report/detail/monthly/MonthlyCategory'
import MonthlyGrowth from '../../../../components/report/detail/monthly/MonthlyGrowth'
import MonthlyHeader from '../../../../components/report/detail/monthly/MonthlyHeader'
import MonthlyLegacy from '../../../../components/report/detail/monthly/MonthlyLegacy'
import MonthlyMember from '../../../../components/report/detail/monthly/MonthlyMember'
import MonthlyProcess from '../../../../components/report/detail/monthly/MonthlyProcess'
import MonthlyResource from '../../../../components/report/detail/monthly/MonthlyResource'
import MonthlyStress from '../../../../components/report/detail/monthly/MonthlyStress'
import HeatmapModal from '../../../../components/report/detail/HeatmapModal'
import { getGetPrams, setPath, setUrlParam } from '../../../../../utils/url'
import { endOfMonth, format, getYear, nextMonth, nowDate, prevMonth, startOfMonth } from '../../../../../utils/date'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../../../states/containers/store'
import { fetchCompare, fetchMonthlyMemberReport, fetchMonthlyTeamReport } from '../../../../../states/containers/report/reportSlice'
import { ResponseMonthlyTeamReport, ResponseMonthlyMemberReport } from '../../../../../states/types/report'
import SearchModal from '../../../../components/report/detail/SearchModal'
import { fetchKeyword, fetchSearchRequest } from '../../../../../states/containers/search/listSlice'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { URL } from '../../../../constants/url'
import Loading from '../../../../components/common/Loading'
import Wordcloud from '../../../../components/report/detail/Wordcloud'
import { SearchRequestParams } from '../../../../../states/types/search'
import PdfModule from '../../../../components/common/PdfModule'

interface HeatmapModal {
  display: boolean
  startDate: string
  endDate: string
  type: string
  target: string
  id: number
  title: string
  legend: string
  startColor: string
  endColor: string
  minimum: number
  displaySearchLink: boolean
  name: string
}

interface SearchModal {
  startDate: string
  endDate: string
  teamIds?: number[]
  teamNames?: string[]
  memberIds?: number[]
  memberNames?: string[]
  keywords?: string[]
  serviceNames?: string[]
  categoryIds?: number[]
  categoryNames?: string[]
  efficiencyLevel?: string[]
  stressLevel?: string[]
  focusLevel?: string[]
  lowerLimitTime?: number
  sortKey?: string
  sortType?: string
}

type LangValue = 'ja' | 'en'

interface GetParams {
  date: string
}

const Index: FC = () => {
  const { target } = useParams<{ target: string | undefined }>()
  const { id } = useParams<{ id: string | undefined }>()
  const { t } = useTranslation()
  const location = useLocation()
  const dispatch = useDispatch()
  const { language } = useParams<{ language: LangValue }>()
  const history = useHistory()

  const LOWER_LIMIT_TIME_DEFAULT = 300

  const {
    login,
    report,
    commonPart,
  } = useSelector((state: RootState) => state)

  const heatmapModalInit = {
    display: false,
    startDate: "",
    endDate: "",
    type: "",
    target: "",
    id: 0,
    title: "",
    legend: "",
    startColor: "",
    endColor: "",
    minimum: 0,
    displaySearchLink: false,
    name: "",
  }

  const searchModalInit = {
    startDate: "",
    endDate: "",
    lowerLimitTime: LOWER_LIMIT_TIME_DEFAULT,
  }

  const [heatmapModal, setHeatmapModal] = useState<HeatmapModal>(heatmapModalInit)
  const [searchModal, setSearchModal] = useState<SearchModal>(searchModalInit)

  const [startDate, setStartDate] = useState<string>()

  const [data, setData] = useState<ResponseMonthlyTeamReport | ResponseMonthlyMemberReport>()

  const [isSearchModalDisplay, setIsSearchModalDisplay] = useState<boolean>(false)

  const [targetYear, setTargetYear] = useState<number>(Number(`${format(prevMonth(nowDate()), "YYYY")}`))

  const [isCalendarDisplay, setIsCalendarDisplay] = useState(false)

  const [isClickable, setIsClickable] = useState(true)

  const month = t('time.date.formats.monthName').split(' ').map((v) => v)

  const [isChange, setIsChange] = useState(false)

  const [targetId, setTargetId] = useState<number>(0)

  const [isDisplaySticky, setIsDisplaySticky] = useState(false)

  const [referrerState, setReferrerState] = useState({
    title: "",
    url: "",
  })

  const pdfTitle = useMemo(() => {
    let nameText = ""
    if (data && target === "team" && "team" in data) {
      nameText = `${data.team?.name}チーム`
    } else if (data && target === "member" && "member" in data) {
      nameText = `${data.member?.name}さん`
    }
    return `Qasee月間改善レポート ${nameText} ${format(startDate, "YYYY年MM月")}`
  }, [data])

  const scrollTop = (): number => Math.max(window.pageYOffset, document.documentElement.scrollTop, document.body.scrollTop)

  const displayHeatmapModal = (display: boolean) => {
    setHeatmapModal({
      ...heatmapModal,
      display: display,
    })
  }

  const heatmapModalDetail = (heatmapDetail: HeatmapModal) => {
    setHeatmapModal({
      ...heatmapModal,
      display: heatmapDetail.display,
      startDate: heatmapDetail.startDate,
      endDate: heatmapDetail.endDate,
      type: heatmapDetail.type,
      target: heatmapDetail.target,
      id: heatmapDetail.id,
      title: heatmapDetail.title,
      legend: heatmapDetail.legend,
      startColor: heatmapDetail.startColor,
      endColor: heatmapDetail.endColor,
      minimum: heatmapDetail.minimum,
      displaySearchLink: heatmapDetail.displaySearchLink,
      name: heatmapDetail.name,
    })
    if (data && "member" in data && data.member) {
      dispatch(fetchCompare(
        {
          start_date: data?.date?.from || startOfMonth(nowDate()),
          end_date: data?.date?.to || endOfMonth(nowDate()),
          target: "member",
          team_id: data?.member?.team_id,
          member_ids: [data.member?.id],
          indicator: heatmapDetail.title,
          name: heatmapDetail.name,
        },
      ))
    } else if (data && "team" in data && data.team) {
      dispatch(fetchCompare(
        {
          start_date: data?.date?.from || startOfMonth(nowDate()),
          end_date: data?.date?.to || endOfMonth(nowDate()),
          target: "team",
          team_id: data?.team.id,
          member_ids: data.team.members.map((member) => member.id),
          indicator: heatmapDetail.title,
          name: heatmapDetail.name,
        },
      ))
    }
  }

  const searchModalDetail = (searchDetail: SearchModal) => {
    setIsSearchModalDisplay(true)
    setSearchModal({
      startDate: searchDetail.startDate,
      endDate: searchDetail.endDate,
      teamIds: searchDetail.teamIds,
      teamNames: searchDetail.teamNames,
      memberIds: searchDetail.memberIds,
      memberNames: searchDetail.memberNames,
      keywords: searchDetail.keywords,
      serviceNames: searchDetail.serviceNames,
      categoryIds: searchDetail.categoryIds,
      categoryNames: searchDetail.categoryNames,
      efficiencyLevel: searchDetail.efficiencyLevel,
      stressLevel: searchDetail.stressLevel,
      focusLevel: searchDetail.focusLevel,
      lowerLimitTime: searchDetail.lowerLimitTime || LOWER_LIMIT_TIME_DEFAULT,
      sortKey: searchDetail.sortKey,
      sortType: searchDetail.sortType,
    })
    const requestParams: SearchRequestParams = {
      start_date: searchDetail.startDate,
      end_date: searchDetail.endDate,
      team_ids: searchDetail.teamIds || [],
      member_ids: searchDetail.memberIds || [],
      keywords: searchDetail.keywords || [],
      service_names: searchDetail.serviceNames || [],
      category_ids: searchDetail.categoryIds || [],
      efficiency_level: searchDetail.efficiencyLevel || [],
      stress_level: searchDetail.stressLevel || [],
      focus_level: searchDetail.focusLevel || [],
      lower_limit_time: searchDetail.lowerLimitTime || LOWER_LIMIT_TIME_DEFAULT,
    }
    if (searchDetail.sortKey) {
      requestParams.sort_key = searchDetail.sortKey
    }
    if (searchDetail.sortType) {
      requestParams.sort_type = searchDetail.sortType
    }
    dispatch(fetchSearchRequest(
      requestParams,
      () => {
        //
      },
    ))
  }

  const handleChangeMonth = (value: string) => {
    setIsCalendarDisplay(false)
    if (!target || !id) return
    setStartDate(format(value))
    setIsClickable(false)
    history.push(`${setPath(setUrlParam(
      URL.REPORT_DETAIL_MONTHLY.path,
      [
        { key: 'target', value: target },
        { key: 'id', value: id },
      ],
    ), language)}?date=${value}`)
  }

  const buildName = () => {
    if (data && target === "team" && "team" in data) {
      return (
        <>
          {data.team?.name}
          チーム
        </>
      )
    }
    if (data && target === "member" && "member" in data) {
      return (
        <>
          {data.member?.name}
          さん
        </>
      )
    }
    return ""
  }

  const onScroll = (): void => {
    if (scrollTop() <= 800) {
      setIsDisplaySticky(false)
    } else {
      setIsDisplaySticky(true)
    }
  }

  useEffect(() => {
    if (report.isLoading) return
    setIsClickable(true)
    if (target === "team" && report.monthlyTeamReport) {
      setData(report.monthlyTeamReport)
      dispatch(fetchKeyword({
        start_date: format(report.monthlyTeamReport.date?.from, "YYYY-MM-DD") || format(startOfMonth(nowDate()), "YYYY-MM-DD"),
        end_date: format(report.monthlyTeamReport.date?.to, "YYYY-MM-DD") || format(endOfMonth(nowDate()), "YYYY-MM-DD"),
        member_ids: report.monthlyTeamReport.team?.members.map((member) => member.id) || [],
      }))
      return
    }
    if (target === "member" && report.monthlyMemberReport) {
      setData(report.monthlyMemberReport)
      dispatch(fetchKeyword({
        start_date: format(report.monthlyMemberReport.date?.from, "YYYY-MM-DD") || format(startOfMonth(nowDate()), "YYYY-MM-DD"),
        end_date: format(report.monthlyMemberReport.date?.to, "YYYY-MM-DD") || format(endOfMonth(nowDate()), "YYYY-MM-DD"),
        member_ids: report.monthlyMemberReport.member?.id ? [report.monthlyMemberReport.member?.id] : [],
      }))
    }
  }, [report.isLoading])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location])

  useEffect(() => {
    if (data?.summary?.evaluation.total_evaluation === "Excellent" && !report.isLoading) {
      document.getElementsByClassName('bg')[0]?.classList.add('excellent')
    } else {
      document.getElementsByClassName('bg')[0]?.classList.remove('excellent')
    }
  }, [data, report.isLoading])

  useEffect(() => {
    const getParams = getGetPrams(location.search) as GetParams
    if (startDate === getParams.date && targetId === Number(id)) return
    if (!getParams.date) {
      setStartDate(prevMonth(nowDate()))
    } else {
      setStartDate(format(getParams.date))
    }
    setTargetId(Number(id))
    setIsChange(true)
  }, [location])

  useEffect(() => {
    if (!login.accessToken || !startDate || !targetId || !isChange) return
    if (target === "team") {
      dispatch(fetchMonthlyTeamReport(targetId, { start_date: format(startDate, "YYYY-MM-DD") }))
    } else {
      dispatch(fetchMonthlyMemberReport(targetId, { start_date: format(startDate, "YYYY-MM-DD") }))
    }
    setIsChange(false)
  }, [isChange])

  useEffect(() => {
    if (!login.accessToken) return
    document.addEventListener("scroll", onScroll)
    // eslint-disable-next-line no-unused-expressions
    document.querySelector('html')?.classList.add('scroll_contents')
  }, [login.accessToken])

  useEffect(() => {
    const state = location.state as { referrer: string }
    if (!state || !state.referrer || !state.referrer.match("/report")) {
      setReferrerState({
        title: "",
        url: "",
      })
      return
    }
    const referrerPath = state.referrer.split(`${language}/`)[1]
    const referrerArray = referrerPath.split("?")[0].split("/")
    let range = ""
    if (referrerArray[1] === "monthly") {
      range = "月間改善レポート"
    } else {
      range = "週間業務レポート"
    }
    let name = ""
    if (referrerArray[2] === "team") {
      name = `${commonPart.teams?.find((team) => team.id === Number(referrerArray[3]))?.name || ""}チーム`
    } else {
      name = `${commonPart.members?.find((member) => member.id === Number(referrerArray[3]))?.name || ""}さん`
    }
    setReferrerState({
      title: `${range} ${name}`,
      url: `${setPath(URL.ROOT.path, language)}${referrerPath}`,
    })
  }, [location])

  return (
    <section className="report_page">
      <Helmet>
        <title>Qasee - AI業務分析 - 月間改善レポート</title>
      </Helmet>
      <section className="breadcrumb">
        {
          data && (
            <>
              <Link
                to={setPath(URL.REPORT.path, language)}
              >
                レポート一覧
              </Link>
              {
                referrerState.title && (
                  <>
                    &nbsp;&gt;&nbsp;
                    <Link
                      to={referrerState.url}
                    >
                      {referrerState.title}
                    </Link>
                  </>
                )
              }
              &nbsp;&gt;&nbsp;月間改善レポート&nbsp;
              {buildName()}
            </>
          )
        }
      </section>
      {
        data && (
          <section className={`sticky_header ${isDisplaySticky ? 'active' : ''}`}>
            <div>
              <div className="bread_area">
                <Link
                  to={setPath(URL.REPORT.path, language)}
                >
                  レポート一覧
                </Link>
                {
                  referrerState.title && (
                    <>
                      &nbsp;&gt;&nbsp;
                      <Link
                        to={referrerState.url}
                      >
                        {referrerState.title}
                      </Link>
                    </>
                  )
                }
                &nbsp;&gt;&nbsp;月間改善レポート&nbsp;
                {buildName()}
              </div>
              <div className="title_area">
                {format(startDate, "YYYY年MM月")}
                &nbsp;月間改善レポート&nbsp;
                {buildName()}
              </div>
            </div>
          </section>
        )
      }
      <section className="page_header">
        <div className="report_title">
          月間改善レポート
        </div>
        <div className="tools">
          <div className="calendar_text">
            <button
              type="button"
              className="display button"
              onClick={() => setIsCalendarDisplay(true)}
              disabled={!isClickable}
            >
              {format(startDate, "YYYY年MM月")}
              <FontAwesomeIcon icon={['fas', 'angle-down']} className={`icon ${isCalendarDisplay && 'open'}`} />
            </button>
            <button
              type="button"
              className="prev button"
              onClick={() => handleChangeMonth(prevMonth(startDate || nowDate(), 'YYYY-MM-DD'))}
              disabled={
                !commonPart.company?.created_at
                || prevMonth(startDate || nowDate(), 'YYYY-MM-DD') === format(startOfMonth(commonPart.company?.created_at), 'YYYY-MM-DD')
                || !isClickable
              }
            >
              <FontAwesomeIcon icon={['fas', 'angle-left']} />
            </button>
            <button
              type="button"
              className="next button"
              onClick={() => handleChangeMonth(nextMonth(startDate || nowDate(), 'YYYY-MM-DD'))}
              disabled={
                !commonPart.company?.created_at
                || nextMonth(startDate || nowDate(), 'YYYY-MM-DD') === format(startOfMonth(nowDate()), 'YYYY-MM-DD')
                || !isClickable
              }
            >
              <FontAwesomeIcon icon={['fas', 'angle-right']} />
            </button>
          </div>
          <div className={`calendar_box ${isCalendarDisplay || 'none'}`}>
            <div className="range">
              <div className="text">
                {t(`range.calendar.header_text.monthly`)}
              </div>
            </div>
            <div className="monthly">
              <div className="select_month">
                <div>
                  <button
                    type="button"
                    onClick={() => setTargetYear(targetYear - 1)}
                    disabled={Number(format(commonPart.company?.created_at, 'YYYY')) >= targetYear}
                  >
                    <FontAwesomeIcon icon={['fas', 'angle-left']} />
                  </button>
                </div>
                <div>
                  {`${targetYear}${t('time.time.short.year')}`}
                </div>
                <div>
                  <button
                    type="button"
                    onClick={() => setTargetYear(targetYear + 1)}
                    disabled={getYear() <= targetYear}
                  >
                    <FontAwesomeIcon icon={['fas', 'angle-right']} />
                  </button>
                </div>
              </div>
              {
                month.map((v, i) => {
                  const targetYearDate = String(targetYear) + (`0${String(i + 1)}`).slice(-2)
                  const isClickableMonth = Number(nowDate('YYYYMM')) > Number(targetYearDate)
                    && Number(format(commonPart.company?.created_at, 'YYYYMM')) <= Number(targetYearDate)
                  const isSelected = format(startDate, 'YYYYMM') === targetYearDate
                  return (
                    <div className="monthly_button" key={v}>
                      <button
                        type="button"
                        className={isSelected ? "selected" : ""}
                        onClick={() => handleChangeMonth(`${String(targetYear)}-${(`0${String(i + 1)}`).slice(-2)}-01`)}
                        disabled={!isClickableMonth}
                      >
                        {`${v}${t('time.time.short.month')}`}
                      </button>
                    </div>
                  )
                })
              }
            </div>
          </div>
          <div className={`close_screen gray ${isCalendarDisplay || 'none'}`} role="presentation" onClick={() => setIsCalendarDisplay(false)} />
        </div>
      </section>
      <section className="page_header">
        <div />
        <div className="tools">
          <PdfModule
            title={pdfTitle}
            target="pdf_area"
          />
        </div>
      </section>
      <section id="pdf_area">
        {
          report.isLoading ? (
            <Loading />
          ) : (
            <>
              {
                target && data?.is_current_data_exist ? (
                  <>
                    <MonthlyHeader
                      target={target}
                      heatmapModalOpenCallback={
                        (heatmap) => heatmapModalDetail({
                          display: true,
                          startDate: data?.date?.from || startOfMonth(nowDate()),
                          endDate: data?.date?.to || endOfMonth(nowDate()),
                          type: heatmap.type,
                          target: heatmap.target,
                          id: heatmap.id,
                          title: heatmap.title,
                          legend: heatmap.legend,
                          startColor: heatmap.startColor,
                          endColor: heatmap.endColor,
                          minimum: heatmap.minimum,
                          displaySearchLink: heatmap.displaySearchLink,
                          name: heatmap.name,
                        })
                      }
                    />
                    <MonthlyGrowth
                      target={target}
                      heatmapModalOpenCallback={
                        (heatmap) => heatmapModalDetail({
                          display: true,
                          type: heatmap.type,
                          startDate: data?.date?.from || startOfMonth(nowDate()),
                          endDate: data?.date?.to || endOfMonth(nowDate()),
                          target: heatmap.target,
                          id: heatmap.id,
                          title: heatmap.title,
                          legend: heatmap.legend,
                          startColor: heatmap.startColor,
                          endColor: heatmap.endColor,
                          minimum: heatmap.minimum,
                          displaySearchLink: heatmap.displaySearchLink,
                          name: heatmap.name,
                        })
                      }
                    />
                    <MonthlyStress
                      target={target}
                      heatmapModalOpenCallback={
                        (heatmap) => heatmapModalDetail({
                          display: true,
                          type: heatmap.type,
                          startDate: data?.date?.from || startOfMonth(nowDate()),
                          endDate: data?.date?.to || endOfMonth(nowDate()),
                          target: heatmap.target,
                          id: heatmap.id,
                          title: heatmap.title,
                          legend: heatmap.legend,
                          startColor: heatmap.startColor,
                          endColor: heatmap.endColor,
                          minimum: heatmap.minimum,
                          displaySearchLink: heatmap.displaySearchLink,
                          name: heatmap.name,
                        })
                      }
                    />
                    <MonthlyResource
                      target={target}
                      heatmapModalOpenCallback={
                        (heatmap) => heatmapModalDetail({
                          display: true,
                          type: heatmap.type,
                          startDate: data?.date?.from || startOfMonth(nowDate()),
                          endDate: data?.date?.to || endOfMonth(nowDate()),
                          target: heatmap.target,
                          id: heatmap.id,
                          title: heatmap.title,
                          legend: heatmap.legend,
                          startColor: heatmap.startColor,
                          endColor: heatmap.endColor,
                          minimum: heatmap.minimum,
                          displaySearchLink: heatmap.displaySearchLink,
                          name: heatmap.name,
                        })
                      }
                    />
                    <MonthlyLegacy
                      target={target}
                      heatmapModalOpenCallback={
                        (heatmap) => heatmapModalDetail({
                          display: true,
                          type: heatmap.type,
                          startDate: data?.date?.from || startOfMonth(nowDate()),
                          endDate: data?.date?.to || endOfMonth(nowDate()),
                          target: heatmap.target,
                          id: heatmap.id,
                          title: heatmap.title,
                          legend: heatmap.legend,
                          startColor: heatmap.startColor,
                          endColor: heatmap.endColor,
                          minimum: heatmap.minimum,
                          displaySearchLink: heatmap.displaySearchLink,
                          name: heatmap.name,
                        })
                      }
                    />
                    <MonthlyProcess
                      target={target}
                      searchModalOpenCallback={
                        (search) => searchModalDetail({
                          startDate: search.startDate,
                          endDate: search.endDate,
                          teamIds: search.teamIds,
                          teamNames: search.teamNames,
                          memberIds: search.memberIds,
                          memberNames: search.memberNames,
                          keywords: search.keywords,
                          serviceNames: search.serviceNames,
                          categoryIds: search.categoryIds,
                          categoryNames: search.categoryNames,
                          efficiencyLevel: search.efficiencyLevel,
                          stressLevel: search.stressLevel,
                          focusLevel: search.focusLevel,
                          sortKey: search.sortKey,
                          sortType: search.sortType,
                        })
                      }
                    />
                    {
                      target === "team" && (
                        <MonthlyMember />
                      )
                    }
                    <MonthlyCategory
                      target={target}
                      searchModalOpenCallback={
                        (search) => searchModalDetail({
                          startDate: search.startDate,
                          endDate: search.endDate,
                          teamIds: search.teamIds,
                          teamNames: search.teamNames,
                          memberIds: search.memberIds,
                          memberNames: search.memberNames,
                          keywords: search.keywords,
                          serviceNames: search.serviceNames,
                          categoryIds: search.categoryIds,
                          categoryNames: search.categoryNames,
                          efficiencyLevel: search.efficiencyLevel,
                          stressLevel: search.stressLevel,
                          focusLevel: search.focusLevel,
                          sortKey: search.sortKey,
                          sortType: search.sortType,
                        })
                      }
                    />
                    <MonthlyApplication
                      target={target}
                      searchModalOpenCallback={
                        (search) => searchModalDetail({
                          startDate: search.startDate,
                          endDate: search.endDate,
                          teamIds: search.teamIds,
                          teamNames: search.teamNames,
                          memberIds: search.memberIds,
                          memberNames: search.memberNames,
                          keywords: search.keywords,
                          serviceNames: search.serviceNames,
                          categoryIds: search.categoryIds,
                          categoryNames: search.categoryNames,
                          efficiencyLevel: search.efficiencyLevel,
                          stressLevel: search.stressLevel,
                          focusLevel: search.focusLevel,
                          sortKey: search.sortKey,
                          sortType: search.sortType,
                        })
                      }
                    />
                    <Wordcloud
                      startDate={data?.date?.from || startOfMonth(nowDate())}
                      endDate={data?.date?.to || endOfMonth(nowDate())}
                    />
                    <HeatmapModal
                      type="monthly"
                      target={target}
                      heatmap={heatmapModal}
                      closeCallback={() => displayHeatmapModal(false)}
                      searchModalOpenCallback={
                        (search) => searchModalDetail({
                          startDate: search.startDate,
                          endDate: search.endDate,
                          teamIds: search.teamIds,
                          teamNames: search.teamNames,
                          memberIds: search.memberIds,
                          memberNames: search.memberNames,
                          keywords: search.keywords,
                          serviceNames: search.serviceNames,
                          categoryIds: search.categoryIds,
                          categoryNames: search.categoryNames,
                          efficiencyLevel: search.efficiencyLevel,
                          stressLevel: search.stressLevel,
                          focusLevel: search.focusLevel,
                          sortKey: search.sortKey,
                          sortType: search.sortType,
                        })
                      }
                    />
                    <SearchModal
                      type="monthly"
                      target={target}
                      searchValue={searchModal}
                      isDisplay={isSearchModalDisplay}
                      closeCallback={() => setIsSearchModalDisplay(false)}
                    />
                  </>
                ) : (
                  <>
                    レポートがありません
                  </>
                )
              }
            </>
          )
        }
      </section>
    </section>
  )
}

export default Index
